// src/App.tsx
import React, { Suspense, lazy, useMemo, useEffect, ComponentType, useState, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { DarkModeProvider } from './context/DarkModeContext';
import { SettingsProvider } from './context/SettingsContext';
import { CareerCardProvider } from './context/CareerCardContext';
import { useAuth, AuthProvider } from './context/AuthContext';
import { ConsentProvider } from './context/ConsentContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import Layout from './components/Layout';
import { DataCacheProvider } from './context/DataCacheContext';
import { initCrashHandlers } from './utils/crashHandler';
import { FeedbackProvider } from './context/FeedbackContext';
import { createLogger } from './utils/logger';
import { setupConsoleCapture } from './utils/consoleCapture';
import { OfflineHandler } from './components/OfflineHandler';
import { updateFileSizes } from './utils/migrations';
import { GoogleAuthProvider } from './context/GoogleAuthContext';
import Retrospectives from './pages/Retrospectives';
// Temporarily import directly to bypass Vite dynamic import issue
import RecordInternshipResults from './pages/RecordInternshipResults';
import { TutorialProvider } from './context/TutorialContext';
import TutorialPanel from './components/TutorialPanel';
import { OnboardingFlow } from './components/OnboardingFlow';
import { ToastProvider, useToast } from './context/ToastContext';
import { analyticsService } from './utils/analyticsService';
import { User } from '@supabase/supabase-js';
import MFAVerification from './components/MFAVerification';
import { supabase } from './utils/supabase';
import { PageHeaderProvider } from './context/PageHeaderContext';
import { StatsigWrapper } from './components/StatsigWrapper';
import StatsigDemo from './pages/StatsigDemo';
import { Page as PrivacySettingsPage } from './pages/settings/privacy/+Page';
import { NotificationProvider } from './context/NotificationContext';
import { AdminWrapper } from './components/AdminWrapper';
import { AdminSettingsProvider } from './context/AdminSettingsContext';
import { addBreadcrumb } from './config/sentry';
import { FocusModeProvider } from './context/FocusModeContext';
import { authCache } from './utils/authCache';

const logger = createLogger({ module: 'App', level: 'info' });

// Helper function to safely access global Statsig instance
const getStatsig = () => {
  if (typeof window !== 'undefined' && (window as any).statsig) {
    return (window as any).statsig;
  }
  return null;
};

// Helper function for Statsig tracking
const trackAppEvent = (eventType: string, metadata?: any) => {
  try {
    const statsig = getStatsig();
    if (statsig) {
      statsig.logEvent('app_event', {
        event_type: eventType,
        ...metadata
      });
    }
  } catch (error) {
    logger.debug('Statsig tracking failed:', { error: error instanceof Error ? error.message : 'Unknown error' });
  }
};

// Define the memory info interface that Chrome exposes
interface MemoryInfo {
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
}

interface ExtendedPerformance extends Performance {
  memory: MemoryInfo;
}

// Infinite retry logic for slow connections with exponential backoff
const retryLazyLoad = (
  importFn: () => Promise<{ default: ComponentType }>,
  moduleName?: string
): Promise<{ default: ComponentType }> => {
  return new Promise((resolve) => {
    let attemptCount = 0;
    let delay = 1000; // Start with 1 second
    
    const attempt = () => {
      attemptCount++;
      
      importFn()
        .then(resolve)
        .catch((error: Error) => {
          // Log the error but don't give up
          if (attemptCount <= 3) {
            console.warn(`Failed to load module${moduleName ? ` (${moduleName})` : ''}, attempt ${attemptCount}:`, error);
          } else if (attemptCount % 10 === 0) {
            // Log every 10th attempt to avoid console spam
            console.warn(`Still trying to load module${moduleName ? ` (${moduleName})` : ''}, attempt ${attemptCount}`);
          }
          
          // Check if we're offline
          if (!navigator.onLine) {
            console.log('Browser is offline, waiting for connection...');
            
            // Wait for online status
            const handleOnline = () => {
              window.removeEventListener('online', handleOnline);
              console.log('Connection restored, retrying module load...');
              delay = 1000; // Reset delay when coming back online
              attempt();
            };
            window.addEventListener('online', handleOnline);
            
            // Also check periodically in case the online event doesn't fire
            setTimeout(() => {
              if (navigator.onLine) {
                window.removeEventListener('online', handleOnline);
                attempt();
              }
            }, 5000);
            return;
          }

          // For network errors, keep retrying with exponential backoff
          // Cap the delay at 30 seconds
          const nextDelay = Math.min(delay * 1.5, 30000);
          
          // Add some jitter to prevent thundering herd
          const jitteredDelay = nextDelay + Math.random() * 1000;
          
          setTimeout(() => {
            attempt();
          }, jitteredDelay);
          
          delay = nextDelay;
        });
    };

    attempt();
  });
};

// Update lazy imports with retry logic and module names
const Landing = lazy(() => retryLazyLoad(() => import('./pages/Landing'), 'Landing'));
const WaitlistFrontDoor = lazy(() => retryLazyLoad(() => import('./pages/WaitlistFrontDoor'), 'WaitlistFrontDoor'));
const Login = lazy(() => retryLazyLoad(() => import('./pages/Login'), 'Login'));
const Home = lazy(() => retryLazyLoad(() => import('./pages/Home'), 'Home'));
const Resumes = lazy(() => retryLazyLoad(() => import('./pages/Resumes'), 'Resumes'));
const JobSearch = lazy(() => retryLazyLoad(() => import('./pages/JobSearchGantt'), 'JobSearch'));
const Profile = lazy(() => retryLazyLoad(() => import('./pages/Profile'), 'Profile'));
const TimelineView = lazy(() => retryLazyLoad(() => import('./pages/TimelineView'), 'TimelineView'));
const Settings = lazy(() => retryLazyLoad(() => import('./pages/Settings'), 'Settings'));
const TermsAndConditions = lazy(() => retryLazyLoad(() => import('./pages/terms/+Page'), 'TermsAndConditions'));
const PrivacyPolicy = lazy(() => retryLazyLoad(() => import('./pages/privacy/+Page'), 'PrivacyPolicy'));
const ResetPassword = lazy(() => retryLazyLoad(() => import('./pages/ResetPassword'), 'ResetPassword'));
const AdminAnalytics = lazy(() => retryLazyLoad(() => import('./pages/AdminAnalytics'), 'AdminAnalytics'));
const AdminNotifications = lazy(() => retryLazyLoad(() => import('./pages/AdminNotifications'), 'AdminNotifications'));
const NotificationAnalytics = lazy(() => retryLazyLoad(() => import('./pages/admin/NotificationAnalytics'), 'NotificationAnalytics'));
const GamificationSupport = lazy(() => retryLazyLoad(() => import('./pages/gamification-support/+Page'), 'GamificationSupport'));
const AdminUsers = lazy(() => retryLazyLoad(() => import('./pages/AdminUsers'), 'AdminUsers'));
const AdminEmails = lazy(() => retryLazyLoad(() => import('./pages/AdminEmails'), 'AdminEmails'));
const InternshipDashboard = lazy(() => retryLazyLoad(() => import('./pages/InternshipDashboard'), 'InternshipDashboard'));
const FocusDashboard = lazy(() => retryLazyLoad(() => import('./pages/FocusDashboard'), 'FocusDashboard'));
const FocusHub = lazy(() => retryLazyLoad(() => import('./pages/FocusHub'), 'FocusHub'));
const FocusSetupPage = lazy(() => retryLazyLoad(() => import('./pages/FocusSetupPage'), 'FocusSetupPage'));
// const RecordInternshipResults = lazy(() => retryLazyLoad(() => import('./pages/RecordInternshipResults'), 'RecordInternshipResults'));
const VerifyEmail = lazy(() => retryLazyLoad(() => import('./pages/VerifyEmail'), 'VerifyEmail'));
const Files = lazy(() => retryLazyLoad(() => import('./pages/Files'), 'Files'));

// Enhanced PageLoader with connection status
const PageLoader = ({ error }: { error?: Error }) => {
  const [loadingTime, setLoadingTime] = useState(0);
  const [isSlowConnection, setIsSlowConnection] = useState(false);
  
  useEffect(() => {
    if (error) {
      trackAppEvent('app_refresh', {
        trigger_type: 'app_error',
        error_type: error.name,
        error_message: error.message
      });
    }
  }, [error]);
  
  // Track loading time
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingTime(prev => {
        const newTime = prev + 1;
        // Consider it a slow connection after 5 seconds
        if (newTime >= 5 && !isSlowConnection) {
          setIsSlowConnection(true);
        }
        return newTime;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isSlowConnection]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center p-4 max-w-md">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4 mx-auto"></div>
        <p className="text-gray-600 mb-2">Loading page...</p>
        
        {isSlowConnection && (
          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-500">
              {!navigator.onLine 
                ? "You appear to be offline. We'll keep trying..."
                : "This is taking longer than usual. Please wait..."}
            </p>
            <p className="text-xs text-gray-400">
              {loadingTime < 30 
                ? `Loading for ${loadingTime} seconds...`
                : "Still trying to load. Your connection might be slow."}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              The page will load automatically when ready.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Add this component to handle MFA verification
const MFAVerificationRoute: React.FC = () => {
  const { user, isMfaPending, currentMfaFactorId, cancelMfaVerification } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const redirectedRef = useRef(false);
  
  // Check for beta token
  const betaToken = localStorage.getItem('beta_access_token');

  useEffect(() => {
    // Debug: Log the current auth state
    logger.debug('MFAVerificationRoute - Current auth state:', {
      user: user ? { id: user.id, email: user.email } : null,
      isMfaPending,
      hasFactorId: !!currentMfaFactorId,
      hasBetaToken: !!betaToken
    });
    
    // Additional check: run getSession to check if we have a session directly with Supabase
    const checkSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.error('Error checking session:', error);
          return;
        }
        
        logger.debug('Session check result:', {
          hasSession: !!data.session,
          hasUser: !!data.session?.user,
          userId: data.session?.user?.id,
          userEmail: data.session?.user?.email
        });
        
        // If we have an authenticated user but isMfaPending is still true, 
        // this could indicate we're stuck in a verification loop
        if (data.session?.user && isMfaPending && !redirectedRef.current) {
          logger.warn('Detected potential MFA verification loop - user has session but MFA still pending');
          
          // Check if storage indicates we're finished with MFA
          try {
            const isPending = sessionStorage.getItem('mfa_verification_pending');
            if (isPending !== 'true') {
              logger.debug('Session storage indicates MFA completed, forcing redirect to home');
              redirectedRef.current = true;
              window.location.href = '/home';
              return;
            }
          } catch (e) {
            logger.warn('Error checking MFA storage state:', e);
          }
        }
      } catch (e) {
        logger.error('Exception in session check:', e);
      }
    };
    
    checkSession();
  }, [user, isMfaPending, currentMfaFactorId, betaToken]);

  // Redirect to login if not in MFA pending state
  useEffect(() => {
    if (!isMfaPending || !currentMfaFactorId) {
      logger.warn('MFA verification not in pending state, redirecting to login', {
        isMfaPending,
        hasFactorId: !!currentMfaFactorId
      });
      
      if (!redirectedRef.current) {
        redirectedRef.current = true;
        navigate('/login');
      }
      return;
    }
    
    // If no beta token, redirect to waitlist
    if (!betaToken) {
      logger.warn('No beta token found during MFA verification, redirecting to waitlist');
      
      if (!redirectedRef.current) {
        redirectedRef.current = true;
        navigate('/');
      }
      return;
    }
  }, [isMfaPending, currentMfaFactorId, navigate, betaToken]);

  const handleSuccess = useCallback(async () => {
    logger.info('MFA verification success handler called');
    setVerifying(true);
    setError(null);
    
    try {
      // Force re-fetch session from Supabase to ensure we have the latest
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        logger.error('Error fetching session after MFA verification:', error);
        throw error;
      }
      
      if (!data.session) {
        logger.error('No session found after successful MFA verification');
        throw new Error('Authentication session not found after MFA verification');
      }
      
      // Ensure beta token is still valid before redirecting
      if (!localStorage.getItem('beta_access_token')) {
        logger.warn('Beta token missing after MFA verification, attempting to set from session');
        
        if (data.session.user?.email) {
          localStorage.setItem('beta_access_token', btoa(data.session.user.email));
          logger.info('Beta token set from session email');
        } else {
          logger.error('Cannot set beta token - no email in session');
          
          if (!redirectedRef.current) {
            redirectedRef.current = true;
            navigate('/');
          }
          return;
        }
      }
      
      // Instead of using setTimeout, force a hard redirect to ensure a clean state
      logger.info('MFA verification successful, redirecting to home page');
      showToast('Verification successful', 'success');
      
      // Manually clear MFA state from session storage as a failsafe
      try {
        sessionStorage.removeItem('mfa_verification_pending');
        sessionStorage.removeItem('mfa_factor_id');
        sessionStorage.removeItem('mfa_email');
        logger.info('Cleared MFA session storage state before redirect');
      } catch (e) {
        logger.warn('Failed to clear MFA storage:', e);
      }
      
      // Prevent multiple redirects
      if (!redirectedRef.current) {
        redirectedRef.current = true;
        
        // Hard redirect to clean state and avoid any issues with React Router
        window.location.href = '/home';
      }
    } catch (error) {
      logger.error('Error in MFA success handler:', error);
      setError('Verification completed but session invalid. Please try logging in again.');
      setVerifying(false);
      
      if (!redirectedRef.current) {
        redirectedRef.current = true;
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    }
  }, [navigate, showToast]);

  const handleError = useCallback((error: Error) => {
    logger.error('MFA verification error handler called:', error);
    setError(error.message || 'Verification failed');
    setVerifying(false);
  }, []);

  const handleCancel = useCallback(() => {
    logger.info('MFA verification cancel handler called');
    cancelMfaVerification();
    
    if (!redirectedRef.current) {
      redirectedRef.current = true;
      navigate('/login');
    }
  }, [cancelMfaVerification, navigate]);

  // Check if user has a valid beta token
  if (!betaToken) {
    logger.warn('No beta token, redirecting from MFAVerificationRoute');
    if (!redirectedRef.current) {
      redirectedRef.current = true;
      return <Navigate to="/" replace />;
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-full max-w-md">
        {(() => {
          // Try to recover factorId from session storage if needed
          let factorIdToUse = currentMfaFactorId;
          
          if (!factorIdToUse || factorIdToUse === "factor_id must be an UUID") {
            // Attempt to recover from session storage
            const storedFactorId = sessionStorage.getItem('mfa_factor_id');
            if (storedFactorId && storedFactorId !== "factor_id must be an UUID") {
              factorIdToUse = storedFactorId;
              logger.info('Recovered factorId from session storage:', { 
                recoveredId: storedFactorId.substring(0, 8) + '...' 
              });
            }
          }
          
          // Add debug logging to check factor ID
          logger.debug('MFAVerificationRoute - checking factor ID before rendering', {
            originalFactorId: currentMfaFactorId,
            finalFactorId: factorIdToUse,
            fromSessionStorage: sessionStorage.getItem('mfa_factor_id'),
            verifying,
            redirected: redirectedRef.current
          });
          
          if (factorIdToUse && !verifying && !redirectedRef.current) {
            return (
              <MFAVerification
                factorId={factorIdToUse}
                onSuccess={handleSuccess}
                onError={handleError}
                onCancel={handleCancel}
              />
            );
          }
          return null;
        })()}
        {verifying && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4 mx-auto"></div>
            <p className="text-gray-600">Completing verification...</p>
          </div>
        )}
        {error && (
          <div className="mt-4 bg-red-100 text-red-700 p-4 rounded">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

// Update ProtectedRoute to check for MFA pending state
const ProtectedRoute: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  const { user, isMfaPending } = useAuth();
  const betaToken = localStorage.getItem('beta_access_token');
  const location = useLocation();
  
  // If we're getting redirected after MFA verification, log details to help debug
  useEffect(() => {
    logger.debug('ProtectedRoute check:', {
      hasUser: !!user,
      userId: user?.id,
      userEmail: user?.email,
      isMfaPending,
      hasBetaToken: !!betaToken,
      betaTokenLength: betaToken ? betaToken.length : 0,
      path: location.pathname,
      comingFrom: location.state?.from || 'unknown'
    });
  }, [user, isMfaPending, betaToken, location]);
  
  // For authenticated users, always ensure a beta token exists
  // This is critical for maintaining session on page refresh
  useEffect(() => {
    if (user?.email && !betaToken) {
      logger.warn('Authenticated user missing beta token - creating one', {
        userId: user.id,
        email: user.email
      });
      
      try {
        localStorage.setItem('beta_access_token', btoa(user.email));
        logger.info('Successfully created beta token for authenticated user');
      } catch (e) {
        logger.error('Failed to create beta token:', e);
      }
    }
  }, [user, betaToken]);
  
  // If no beta token but user is authenticated, create a beta token first
  if (!betaToken && user?.email) {
    logger.warn('Authenticated user missing beta token in ProtectedRoute render - creating one first');
    try {
      localStorage.setItem('beta_access_token', btoa(user.email));
      // Return the element directly since we now have a beta token
      logger.info('Created beta token for authenticated user in ProtectedRoute');
      
      // Force a refresh of the current path to ensure proper routing after token creation
      window.location.href = window.location.pathname;
      return <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Applying authentication...</p>
        </div>
      </div>;
    } catch (e) {
      logger.error('Failed to create beta token in ProtectedRoute:', e);
    }
  }
  
  if (!localStorage.getItem('beta_access_token')) {
    logger.warn('No beta token available in ProtectedRoute despite user being authenticated, creating one more time');
    
    try {
      if (user?.email) {
        localStorage.setItem('beta_access_token', btoa(user.email));
        logger.info('Created beta token as last resort');
        
        // Force refresh to apply the token
        window.location.href = window.location.pathname;
        return <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Restoring session...</p>
          </div>
        </div>;
      }
      
      logger.error('Cannot create beta token - no email in user object');
      return <Navigate to="/" replace />;
    } catch (e) {
      logger.error('Failed to create beta token as last resort:', e);
      return <Navigate to="/" replace />;
    }
  }
  
  // If MFA is pending AND we have a valid factor ID, redirect to MFA verification
  const mfaFactorId = sessionStorage.getItem('mfa_factor_id');
  const hasValidFactorId = !!mfaFactorId && mfaFactorId !== "factor_id must be an UUID";
  
  if (user && isMfaPending && hasValidFactorId) {
    logger.info('MFA pending with valid factor ID in ProtectedRoute, redirecting to verification');
    return <Navigate to="/verify-mfa" replace />;
  }
  
  // If MFA is pending but NO valid factor ID, clear the flags to unblock the user
  if (user && isMfaPending && !hasValidFactorId) {
    logger.warn('MFA pending but no valid factor ID in ProtectedRoute - clearing MFA flags');
    try {
      sessionStorage.removeItem('mfa_signin_in_progress');
      sessionStorage.removeItem('mfa_verification_pending');
      sessionStorage.removeItem('mfa_factor_id');
      // Don't clear user directly but let the AuthContext update properly
    } catch (e) {
      logger.error('Failed to clear MFA flags in ProtectedRoute:', e);
    }
  }
  
  // All checks passed, render the protected element
  return <>{element}</>;
};

// Update RouteWithErrorBoundary to handle loading errors better and properly type the element
const RouteWithErrorBoundary: React.FC<{ element: React.ReactNode }> = ({ element }) => (
  <ErrorBoundary>
    <Suspense 
      fallback={<PageLoader />}
    >
      {element}
    </Suspense>
  </ErrorBoundary>
);

// Add this function before AppRoutes
const getLocalOnboardingStatus = (userId: string): boolean => {
  try {
    const key = `onboarding_complete_${userId}`;
    return localStorage.getItem(key) === 'true';
  } catch (e) {
    return false;
  }
};

const setLocalOnboardingStatus = (userId: string, status: boolean): void => {
  try {
    const key = `onboarding_complete_${userId}`;
    localStorage.setItem(key, status.toString());
  } catch (e) {
    // Ignore errors with localStorage
  }
};

// Add this new component for admin route protection
const AdminProtectedRoute: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      try {
        const isUserAdmin = await analyticsService.isAnalyticsAdmin(user.id);
        setIsAdmin(isUserAdmin);
      } catch (error) {
        console.error('Failed to check admin status', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, [user]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Checking permissions...</p>
        </div>
      </div>
    );
  }
  
  if (!isAdmin) {
    return <Navigate to="/home" replace />;
  }
  
  return <>{element}</>;
};

// Add logout route handler
const LogoutHandler: React.FC = () => {
  const { signOut } = useAuth();
  
  useEffect(() => {
    const handleLogout = async () => {
      try {
        await signOut();
      } catch (error) {
        console.error('Error during logout:', error);
        // Force redirect to landing on error
        window.location.href = '/';
      }
    };
    
    handleLogout();
  }, [signOut]);
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Logging out...</p>
      </div>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  const { user, loading, onboardingComplete, onboardingLoading, isMfaPending, updateOnboardingStatus } = useAuth();
  const location = useLocation();
  
  // Track navigation
  useEffect(() => {
    addBreadcrumb(
      `Navigation to ${location.pathname}`,
      'navigation',
      {
        to: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      'info'
    );
  }, [location]);
  
  // Prevent flickering during auth state changes by adding stability
  const [stableAuthState, setStableAuthState] = useState<{
    user: User | null,
    loading: boolean,
    onboardingComplete: boolean,
    isMfaPending: boolean
  }>({
    user: null,
    loading: true,
    onboardingComplete: false,
    isMfaPending: false
  });
  
  // Track if we've had auth before to prevent routes from flickering during auth state changes
  const hadAuthRef = useRef(false);
  
  // CRITICAL FIX: Add initial auth check to prevent flashing to WaitlistFrontDoor
  const [initialAuthChecked, setInitialAuthChecked] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  
  // Check for existing authentication immediately on mount
  useEffect(() => {
    const checkInitialAuth = async () => {
      try {
        // Check for beta token first (quick check)
        const betaToken = localStorage.getItem('beta_access_token');
        
        // Check for recent auth session
        const hasHadAuthSession = sessionStorage.getItem('has_had_auth_session') === 'true';
        
        // Check Supabase session
        const { data: { session } } = await supabase.auth.getSession();
        
        const hasAuth = !!(session?.user && betaToken);
        
        logger.debug('Initial auth check results:', {
          hasSession: !!session,
          hasBetaToken: !!betaToken,
          hasHadAuthSession,
          hasAuth,
          userEmail: session?.user?.email
        });
        
        setHasValidSession(hasAuth);
        
        // If we have auth, set hadAuthRef to prevent flickering
        if (hasAuth) {
          hadAuthRef.current = true;
        }
      } catch (error) {
        logger.error('Error in initial auth check:', error);
        setHasValidSession(false);
      } finally {
        setInitialAuthChecked(true);
      }
    };
    
    checkInitialAuth();
  }, []);
  
  // Stabilize auth state
  useEffect(() => {
    // Check if we just completed authentication and reloaded the page
    const authCompleted = sessionStorage.getItem('auth_completed') === 'true';
    const authSuccessTimestamp = sessionStorage.getItem('auth_success_timestamp');
    
    if (authCompleted && authSuccessTimestamp) {
      // If auth was completed more than 5 minutes ago, it might be stale
      const timestamp = parseInt(authSuccessTimestamp, 10);
      const isStale = Date.now() - timestamp > 5 * 60 * 1000; // 5 minutes
      
      if (!isStale) {
        logger.info('Detected successful auth reload, clearing auth_completed flag');
        // Clear the flag to prevent it from being processed again
        sessionStorage.removeItem('auth_completed');
        sessionStorage.removeItem('auth_success_timestamp');
        
        // Also clear any other auth flags that might have survived the reload
        sessionStorage.removeItem('auth_flow_in_progress');
        sessionStorage.removeItem('auth_flow_email');
        sessionStorage.removeItem('auth_flow_timestamp');
      } else {
        logger.warn('Found stale auth_completed flag, clearing it');
        sessionStorage.removeItem('auth_completed');
        sessionStorage.removeItem('auth_success_timestamp');
      }
    }

    if (loading) return; // Don't update during loading
    
    // If we've had auth before, don't switch to unauthenticated state too quickly
    // to prevent route flickering during page transitions
    if (hadAuthRef.current && !user && location.pathname.startsWith('/home')) {
      logger.debug('Stabilizing auth state during transition', {
        hadAuth: hadAuthRef.current,
        hasUser: !!user,
        path: location.pathname
      });
      
      // Give it time for auth to recover before changing routes
      const timer = setTimeout(() => {
        setStableAuthState({ user, loading, onboardingComplete, isMfaPending });
      }, 500);
      
      return () => clearTimeout(timer);
    }
    
    if (user) {
      hadAuthRef.current = true;
      
      // Ensure beta token is set when we have a user
      if (user.email) {
        try {
          const betaToken = localStorage.getItem('beta_access_token');
          if (!betaToken) {
            logger.warn('User authenticated in AppRoutes but missing beta token - creating one', {
              userId: user.id,
              email: user.email
            });
            localStorage.setItem('beta_access_token', btoa(user.email));
          }
        } catch (e) {
          logger.error('Failed to ensure beta token in AppRoutes', e);
        }
      }
      
      // If we get a user, update immediately
      setStableAuthState({ user, loading, onboardingComplete, isMfaPending });
    } else {
      // If we've never had auth before, or we're on a public route, update immediately
      setStableAuthState({ user, loading, onboardingComplete, isMfaPending });
    }
  }, [user, loading, onboardingComplete, isMfaPending, location.pathname]);
  
  // Check localStorage as a backup for onboarding status
  const localOnboardingStatus = stableAuthState.user ? getLocalOnboardingStatus(stableAuthState.user.id) : false;
  
  // Use the more stable version for determining onboarding status
  const effectiveOnboardingComplete = stableAuthState.onboardingComplete || localOnboardingStatus;
  
  // Update localStorage when onboardingComplete changes
  useEffect(() => {
    if (user && onboardingComplete) {
      setLocalOnboardingStatus(user.id, true);
    }
  }, [user, onboardingComplete]);
  
  logger.debug('AppRoutes render:', { 
    loading, 
    onboardingLoading, 
    user: user ? { id: user.id, email: user.email } : null, 
    onboardingComplete,
    localOnboardingStatus,
    effectiveOnboardingComplete,
    isMfaPending,
    initialAuthChecked,
    hasValidSession
  });

  // CRITICAL FIX: Show loading screen until initial auth check is complete
  // This prevents flashing to WaitlistFrontDoor when user is actually authenticated
  if (!initialAuthChecked || loading || (user && onboardingLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-r from-blue-500 to-purple-600">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading your career journey...</p>
          <p className="text-white text-sm mt-2 opacity-80">
            {!initialAuthChecked ? "Checking authentication..." : loading ? "Authenticating..." : "Preparing your workspace..."}
          </p>
        </div>
      </div>
    );
  }

  // Additional safety check - directly check session storage for MFA pending state and FACTOR ID
  // This provides protection even before React state has updated
  const mfaPendingInStorage = sessionStorage.getItem('mfa_verification_pending') === 'true';
  const mfaSignInInProgress = sessionStorage.getItem('mfa_signin_in_progress') === 'true';
  const mfaFactorId = sessionStorage.getItem('mfa_factor_id');
  const hasValidFactorId = !!mfaFactorId && mfaFactorId !== "factor_id must be an UUID";
  
  // Check for auth flow in progress (new stability feature for all users)
  const authFlowInProgress = sessionStorage.getItem('auth_flow_in_progress') === 'true';
  const authFlowEmail = sessionStorage.getItem('auth_flow_email');
  const authFlowTimestamp = sessionStorage.getItem('auth_flow_timestamp');
  
  // Check if auth flow is stale (more than 3 seconds old for non-MFA users)
  const isAuthFlowStale = authFlowInProgress && authFlowTimestamp && 
    (Date.now() - parseInt(authFlowTimestamp, 10) > 3000);
  
  // If auth flow is stale, clear the flags to prevent being stuck
  if (isAuthFlowStale) {
    logger.warn('Found stale auth flow flags, clearing them to prevent being stuck', {
      authFlowEmail,
      authFlowTimestamp,
      timeSinceStart: authFlowTimestamp ? (Date.now() - parseInt(authFlowTimestamp, 10)) / 1000 + 's' : 'unknown'
    });
    
    // IMPORTANT: Clear ALL session storage flags that might keep us in a loading state
    sessionStorage.removeItem('auth_flow_in_progress');
    sessionStorage.removeItem('auth_flow_email');
    sessionStorage.removeItem('auth_flow_timestamp');
    
    // Also clear any MFA flags that might be causing issues
    sessionStorage.removeItem('mfa_signin_in_progress');
    sessionStorage.removeItem('mfa_verification_pending');
    sessionStorage.removeItem('mfa_factor_id');
    sessionStorage.removeItem('mfa_email');
    sessionStorage.removeItem('mfa_ultra_protection');
    
    // Force a route reload instead of waiting for React state update
    // This ensures we don't get stuck in loading state
    if (stableAuthState.user) {
      logger.info('Forcing navigation to /home to recover from stale auth flow');
      window.location.href = '/home';
      
      // Show temporary loading message while redirecting
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Redirecting to home page...</p>
          </div>
        </div>
      );
    }
  }
  
  // COMPLETELY NEW APPROACH: If auth flow is active and we're at the login page,
  // simply redirect to home instead of showing a loading screen.
  // This prevents users from getting stuck in the loading state.
  if (authFlowInProgress && !isAuthFlowStale && location.pathname === '/login' && stableAuthState.user) {
    logger.info('Auth flow in progress with valid user detected - redirecting to home instead of showing loading screen', {
      authFlowEmail,
      authFlowTimestamp,
      timeSinceStart: authFlowTimestamp ? (Date.now() - parseInt(authFlowTimestamp, 10)) / 1000 + 's' : 'unknown',
      userId: stableAuthState.user.id
    });
    
    // Clear the auth flow flags since we're redirecting
    sessionStorage.removeItem('auth_flow_in_progress');
    sessionStorage.removeItem('auth_flow_email');
    sessionStorage.removeItem('auth_flow_timestamp');
    
    // Clear any MFA flags that might be incorrectly set
    sessionStorage.removeItem('mfa_signin_in_progress');
    sessionStorage.removeItem('mfa_verification_pending');
    sessionStorage.removeItem('mfa_factor_id');
    sessionStorage.removeItem('mfa_email');
    
    return <Navigate to="/home" replace />;
  }
  
  // Ensure we immediately redirect authenticated users to /home if auth flow is active
  // This prevents users from getting stuck on the loading screen
  if (authFlowInProgress && stableAuthState.user) {
    logger.info('Auth flow in progress but user is authenticated - redirecting to home page', {
      authFlowEmail,
      authFlowTimestamp,
      timeSinceStart: authFlowTimestamp ? (Date.now() - parseInt(authFlowTimestamp, 10)) / 1000 + 's' : 'unknown',
      userId: stableAuthState.user.id,
      path: location.pathname
    });
    
    // Clear auth flags since we're redirecting
    sessionStorage.removeItem('auth_flow_in_progress');
    sessionStorage.removeItem('auth_flow_email');
    sessionStorage.removeItem('auth_flow_timestamp');
    
    // Only redirect if we're not already on the home page
    if (location.pathname !== '/home') {
      return <Navigate to="/home" replace />;
    }
  }
  
  // Only show loading screen if we're NOT on the login page, which helps prevent getting stuck
  if (authFlowInProgress && !isAuthFlowStale && !stableAuthState.user) {
    logger.info('Auth flow in progress, showing loading screen to allow auth to stabilize', {
      authFlowEmail,
      authFlowTimestamp,
      timeSinceStart: authFlowTimestamp ? (Date.now() - parseInt(authFlowTimestamp, 10)) / 1000 + 's' : 'unknown'
    });
    
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Establishing secure session...</p>
          <p className="text-sm text-gray-500 mt-2">This will only take a moment</p>
        </div>
      </div>
    );
  }
  
  // DEBUG: Log the MFA flags to help diagnose routing issues
  logger.debug('AppRoutes MFA flags check:', { 
    mfaPendingInStorage, 
    mfaSignInInProgress,
    hasValidFactorId,
    factorIdValue: mfaFactorId ? `${mfaFactorId.substring(0, 8)}...` : 'none',
    isMfaPendingState: stableAuthState.isMfaPending,
    hasUser: !!stableAuthState.user,
    pathname: location.pathname
  });
  
  // Fix: If these flags are active but we have a session without a valid factor ID, clear them
  // This prevents getting stuck in MFA flow for non-MFA users
  if (stableAuthState.user && (mfaSignInInProgress || mfaPendingInStorage) && !hasValidFactorId) {
    logger.warn('Found MFA flags but no valid factor ID with authenticated user. Clearing all MFA flags.');
    try {
      sessionStorage.removeItem('mfa_signin_in_progress');
      sessionStorage.removeItem('mfa_verification_pending');
      sessionStorage.removeItem('mfa_factor_id');
      sessionStorage.removeItem('mfa_email');
    } catch (e) {
      logger.error('Failed to clear stale MFA flags:', e);
    }
  }
  
  // If MFA verification is needed AND we have a valid factor ID, render the verification route
  // Both conditions must be met to prevent showing MFA UI without a valid factor ID
  if (stableAuthState.user && sessionStorage.getItem('mfa_factor_id') && 
   (sessionStorage.getItem('mfa_verification_pending') === 'true' || 
    sessionStorage.getItem('mfa_signin_in_progress') === 'true')) {
    logger.debug('User authenticated with MFA flow active and valid factor ID, checking if redirect needed');
    
    // Only redirect if we're not already on the MFA page
    if (location.pathname !== '/verify-mfa') {
      logger.debug('Redirecting to MFA verification page');
      return (
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/verify-mfa" element={<MFAVerificationRoute />} />
              <Route path="*" element={<Navigate to="/verify-mfa" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      );
    }
  }

  // Check for beta token - this is required for access to the app
  // We'll add this directly in the main routing logic to prevent incorrect redirects
  const hasBetaToken = !!localStorage.getItem('beta_access_token');
  
  // Debug log so we can see if beta token is the issue
  logger.debug('AppRoutes beta token check:', { 
    hasBetaToken,
    hasUser: !!stableAuthState.user,
    pathname: location.pathname
  });
  
  // If user is authenticated but missing beta token, update it
  if (stableAuthState.user && !hasBetaToken && stableAuthState.user.email) {
    logger.warn('User is authenticated but missing beta token - creating one');
    try {
      localStorage.setItem('beta_access_token', btoa(stableAuthState.user.email));
      logger.debug('Created beta token for authenticated user');
    } catch (e) {
      logger.error('Failed to create beta token:', e);
    }
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/" 
            element={
              // CRITICAL FIX: Use hasValidSession to prevent flashing to WaitlistFrontDoor
              (stableAuthState.user || hasValidSession) ? <Navigate to="/home" replace /> : <WaitlistFrontDoor />
            } 
          />
          <Route 
            path="/landing" 
            element={
              (stableAuthState.user || hasValidSession) ? <Navigate to="/home" replace /> : <Landing />
            } 
          />
          <Route 
            path="/login" 
            element={
              (stableAuthState.user || hasValidSession) && !stableAuthState.isMfaPending ? <Navigate to="/home" replace /> : <Login />
            } 
          />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/gamification-support" element={
            <RouteWithErrorBoundary element={<GamificationSupport />} />
          } />
          <Route 
            path="/reset-password" 
            element={
              <RouteWithErrorBoundary element={<ResetPassword />} />
            } 
          />
          <Route 
            path="/verify-email" 
            element={
              <RouteWithErrorBoundary element={<VerifyEmail />} />
            } 
          />
          
          {/* MFA Verification Route - Always available when user exists */}
          {user && (
            <Route path="/verify-mfa" element={
              sessionStorage.getItem('mfa_factor_id') && 
              (sessionStorage.getItem('mfa_verification_pending') === 'true' || 
               sessionStorage.getItem('mfa_signin_in_progress') === 'true') ? (
                <MFAVerificationRoute />
              ) : (
                <Navigate to="/home" replace />
              )
            } />
          )}
          
          {/* Add explicit logout route */}
          <Route path="/logout" element={<LogoutHandler />} />
          
          {/* Add dedicated onboarding route that's only accessible during signup */}
          <Route path="/onboarding" element={
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                {(user || hasValidSession) ? <OnboardingFlow onComplete={updateOnboardingStatus} /> : <Navigate to="/login" replace />}
              </Suspense>
            </ErrorBoundary>
          } />

          {/* Authenticated Routes */}
          {(user || hasValidSession) && (
            <Route element={<ProtectedRoute element={
              <GoogleAuthProvider>
                <PageHeaderProvider>
                  <Layout />
                </PageHeaderProvider>
              </GoogleAuthProvider>
            } />}>
              <Route index element={<Navigate to="/home" replace />} />
              <Route path="home" element={
                <RouteWithErrorBoundary element={
                  <Home />
                } />
              } />
              <Route path="retrospectives/*" element={
                <RouteWithErrorBoundary element={
                  <Retrospectives />
                } />
              } />
              <Route path="resumes" element={
                <RouteWithErrorBoundary element={
                  <Resumes />
                } />
              } />
              <Route path="job-search" element={
                <RouteWithErrorBoundary element={
                  <JobSearch />
                } />
              } />
              <Route path="timeline" element={
                <RouteWithErrorBoundary element={
                  <TimelineView />
                } />
              } />
              <Route path="profile" element={
                <RouteWithErrorBoundary element={
                  <Profile />
                } />
              } />
              <Route path="settings" element={
                <RouteWithErrorBoundary element={
                  <Settings />
                } />
              } />
              <Route path="settings/privacy" element={
                <RouteWithErrorBoundary element={
                  <PrivacySettingsPage />
                } />
              } />
              <Route path="files" element={
                <RouteWithErrorBoundary element={
                  <Files />
                } />
              } />
              <Route path="focus" element={
                <RouteWithErrorBoundary element={
                  <FocusHub />
                } />
              } />
              <Route path="focus/:focusType/setup" element={
                <RouteWithErrorBoundary element={
                  <FocusSetupPage />
                } />
              } />
              {/* New route with focus ID for multiple focuses of same type */}
              <Route path="focus/:focusType/:focusId" element={
                <RouteWithErrorBoundary element={
                  <FocusDashboard />
                } />
              } />
              {/* Keep old route for backward compatibility */}
              <Route path="focus/:focusType" element={
                <RouteWithErrorBoundary element={
                  <FocusDashboard />
                } />
              } />
              {/* Keep the direct internship route for backward compatibility */}
              <Route path="focus/internship" element={
                <RouteWithErrorBoundary element={
                  <InternshipDashboard />
                } />
              } />
              {/* Internship Results Recording */}
              <Route path="focus/internship/:focusId/results/new" element={
                <ProtectedRoute element={
                  <RouteWithErrorBoundary element={
                    <RecordInternshipResults />
                  } />
                } />
              } />
              <Route path="admin/analytics" element={
                <AdminProtectedRoute element={
                  <RouteWithErrorBoundary element={
                    <AdminAnalytics />
                  } />
                } />
              } />
              <Route path="admin/notifications" element={
                <AdminProtectedRoute element={
                  <RouteWithErrorBoundary element={
                    <AdminNotifications />
                  } />
                } />
              } />
              <Route path="admin/notification-analytics" element={
                <AdminProtectedRoute element={
                  <RouteWithErrorBoundary element={
                    <NotificationAnalytics />
                  } />
                } />
              } />
              <Route path="admin/users" element={
                <AdminProtectedRoute element={
                  <RouteWithErrorBoundary element={
                    <AdminUsers />
                  } />
                } />
              } />
              <Route path="admin/emails" element={
                <AdminProtectedRoute element={
                  <RouteWithErrorBoundary element={
                    <AdminEmails />
                  } />
                } />
              } />
              <Route path="statsig-demo" element={
                <AdminProtectedRoute element={
                  <RouteWithErrorBoundary element={
                    <StatsigDemo />
                  } />
                } />
              } />
            </Route>
          )}

          {/* Catch-all route for unmatched paths */}
          <Route path="*" element={
            (user || hasValidSession) ? (
              <Navigate to="/home" replace />
            ) : (
              <Navigate to="/" replace />
            )
          } />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

const AppRoutesWrapper: React.FC = () => {
  const { user, loading, onboardingComplete, onboardingLoading, isMfaPending } = useAuth();

  // Initialize authCache subscription to keep it in sync with auth state
  useEffect(() => {
    logger.debug('Initializing authCache subscription');
    const unsubscribe = authCache.subscribeToAuthChanges();
    
    return () => {
      logger.debug('Cleaning up authCache subscription');
      unsubscribe();
    };
  }, []);

  const memoizedRoutes = useMemo(() => (
    <AppRoutes />
  ), [loading, user, onboardingComplete, onboardingLoading, isMfaPending]);

  return memoizedRoutes;
};

const App: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Initialize crash handlers and console capture
    initCrashHandlers();
    setupConsoleCapture();


    // WebSocket monitoring removed - using polling instead

    // Set up performance monitoring
    if ('memory' in performance) {
      const memoryThreshold = 0.9; // 90% memory usage threshold
      let lastReportTime = 0;
      const reportInterval = 300000; // Only report every 5 minutes

      const checkMemory = () => {
        const perf = performance as ExtendedPerformance;
        const usageRatio = perf.memory.usedJSHeapSize / perf.memory.jsHeapSizeLimit;
        const now = Date.now();
        
        // Only log if memory is high and we haven't reported recently
        if (usageRatio > memoryThreshold && now - lastReportTime > reportInterval) {
          lastReportTime = now;
          logger.debug('High memory usage detected:', {
            usageRatio,
            jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
            totalJSHeapSize: perf.memory.totalJSHeapSize,
            usedJSHeapSize: perf.memory.usedJSHeapSize
          });

          // Track metrics but don't take action
          trackAppEvent('app_refresh', {
            trigger_type: 'browser_memory',
            memory_usage: {
              jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
              totalJSHeapSize: perf.memory.totalJSHeapSize,
              usedJSHeapSize: perf.memory.usedJSHeapSize
            }
          });
        }
      };

      const memoryCheckInterval = setInterval(checkMemory, 30000); // Check every 30 seconds
      
      // Cleanup on unmount
      return () => {
        clearInterval(memoryCheckInterval);
        
        // WebSocket monitoring removed - using polling instead
        
        // Import and shutdown app services
        import('./services/appInitialization').then(({ shutdownApp }) => {
          shutdownApp().catch(error => {
            logger.warn('Failed to shutdown app services on unmount:', error);
          });
        });
      };
    }
  }, []);

  // Track route changes
  useEffect(() => {
    const handleBeforeUnload = () => {
      trackAppEvent('app_refresh', {
        trigger_type: 'user_initiated',
        route: location.pathname,
        metadata: { action: 'page_refresh' }
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [location]);

  useEffect(() => {
    // Run migrations
    updateFileSizes();
  }, []);

  logger.debug('App render');

  return (
    <ErrorBoundary 
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center p-4">
            <p className="text-red-600 mb-4">Something went wrong</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Retry
            </button>
          </div>
        </div>
      }
    >
      <ToastProvider>
        <SettingsProvider>
          <StatsigWrapper>
            <AuthProvider>
              <ConsentProvider>
                <DataCacheProvider>
                  <DarkModeProvider>
                    <CareerCardProvider>
                      <FocusModeProvider>
                        <FeedbackProvider>
                          <TutorialProvider>
                            <AdminSettingsProvider>
                              <OfflineHandler />
                              <NotificationProvider>
                                <AdminWrapper>
                                  <AppRoutesWrapper />
                                </AdminWrapper>
                              </NotificationProvider>
                              <TutorialPanel />
                            </AdminSettingsProvider>
                          </TutorialProvider>
                        </FeedbackProvider>
                      </FocusModeProvider>
                    </CareerCardProvider>
                  </DarkModeProvider>
                </DataCacheProvider>
              </ConsentProvider>
            </AuthProvider>
          </StatsigWrapper>
        </SettingsProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default React.memo(App);